import type { AxiosRequestConfig } from 'axios';
import { api, setTokenGetter } from './client';
import { resolveTenantCode } from './envConfig';

export interface ApiConfig {
  /** Returns the current bearer token (or null when signed out). */
  getToken?: () => string | null;
  /** Returns the current refresh token, enabling silent renewal. Null disables it. */
  getRefreshToken?: () => string | null;
  /** Store a renewed session. Called with the new access + refresh tokens after a silent renewal. */
  onRefreshed?: (session: { token: string; refreshToken: string; sessionExpiresAt?: string }) => void;
  /** Called when the API returns 401 so the app can sign the user out. */
  onUnauthorized?: () => void;
}

interface RefreshResult {
  token: string;
  refreshToken: string;
  sessionExpiresAt?: string;
}

/**
 * Wire app state (auth token, silent refresh, unauthorized handler) into the axios client.
 * Call once at bootstrap.
 *
 * A 401 used to mean an immediate logout, so an access token expiring mid-shift dropped the user
 * on the login screen with whatever they were doing lost. Now the first 401 tries to renew the
 * session behind the scenes and replays the original request; only when the renewal itself fails
 * — the refresh token is spent, revoked, or the session's hard limit is up — does the app sign out.
 */
export function configureApi(config: ApiConfig): void {
  if (config.getToken) setTokenGetter(config.getToken);

  // One in-flight renewal shared by every request that 401s at the same moment. Without this, a
  // screen firing six parallel calls would start six renewals; the tokens rotate, so five of them
  // would present an already-spent token and be refused — turning a renewable session into a
  // logout.
  let inFlight: Promise<RefreshResult | null> | null = null;

  async function renew(): Promise<RefreshResult | null> {
    const refreshToken = config.getRefreshToken?.() ?? null;
    if (!refreshToken) return null;
    try {
      // Bare axios call, not `api`: this must not recurse through this interceptor.
      const res = await api.post<{ success: boolean; result: RefreshResult }>(
        '/auth/refresh',
        { refreshToken, tenantCode: resolveTenantCode() },
        { headers: { 'X-Skip-Refresh': '1' } }
      );
      const next = res.data?.result;
      if (!next?.token || !next?.refreshToken) return null;
      config.onRefreshed?.(next);
      return next;
    } catch {
      return null;
    }
  }

  api.interceptors.response.use(
    (res) => res,
    async (err) => {
      const status = err?.response?.status;
      const cfg = err?.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
      const skip = cfg?.headers?.['X-Skip-Refresh'] === '1';

      if (status === 401 && cfg && !cfg._retried && !skip && config.getRefreshToken?.()) {
        cfg._retried = true; // one attempt per request, so a persistent 401 can't loop
        inFlight = inFlight ?? renew().finally(() => { inFlight = null; });
        const renewed = await inFlight;
        if (renewed) {
          cfg.headers = { ...(cfg.headers ?? {}), Authorization: `Bearer ${renewed.token}` };
          return api.request(cfg);
        }
      }

      if (status === 401) config.onUnauthorized?.();
      return Promise.reject(err);
    }
  );
}
